// Country and currency mapping config. Voucher records use the same legacy
// country values as provider-synced vouchers, which keeps admin filters unique.
export const COUNTRIES_CURRENCY_MAP = {
  'CN': 'CNY',
  'china': 'CNY',
  'FR': 'EUR',
  'france': 'EUR',
  'HK': 'HKD',
  'hong_kong': 'HKD',
  'ID': 'IDR',
  'indonesia': 'IDR',
  'MY': 'MYR',
  'malaysia': 'MYR',
  'SG': 'SGD',
  'singapore': 'SGD',
  'TH': 'THB',
  'thailand': 'THB',
  'US': 'USD',
  'usa': 'USD',
  'JP': 'JPY',
  'japan': 'JPY',
  'KR': 'KRW',
  'south_korea': 'KRW',
  'VN': 'VND',
  'vietnam': 'VND',
  'PH': 'PHP',
  'philippines': 'PHP',
  'AU': 'AUD',
  'australia': 'AUD',
  'GB': 'GBP',
  'uk': 'GBP'
};

// Store legacy country values so manually-created vouchers share the same
// filter buckets as provider-synced vouchers.
export const COUNTRY_OPTIONS = [
  { value: 'australia', label: 'Australia' },
  { value: 'china', label: 'China' },
  { value: 'france', label: 'France' },
  { value: 'hong_kong', label: 'Hong Kong' },
  { value: 'indonesia', label: 'Indonesia' },
  { value: 'japan', label: 'Japan' },
  { value: 'malaysia', label: 'Malaysia' },
  { value: 'philippines', label: 'Philippines' },
  { value: 'singapore', label: 'Singapore' },
  { value: 'south_korea', label: 'South Korea' },
  { value: 'thailand', label: 'Thailand' },
  { value: 'uk', label: 'United Kingdom' },
  { value: 'usa', label: 'United States' },
  { value: 'vietnam', label: 'Vietnam' }
].sort((a, b) => a.label.localeCompare(b.label));

// Voucher category values aligned 1:1 with VIO's Voucher.category enum
// (vio-v4-api/src/models/Voucher.js). VIO is the source of truth, so we store
// the exact display strings and sync them across without remapping.
export const VOUCHER_CATEGORY_OPTIONS = [
  { value: 'Wellness', label: 'Wellness' },
  { value: 'Health', label: 'Health' },
  { value: 'Food & Beverage', label: 'Food & Beverage' },
  { value: 'Leisure & Entertainment', label: 'Leisure & Entertainment' },
  { value: 'Travel & Hospitality', label: 'Travel & Hospitality' },
  { value: 'Lifestyle & Services', label: 'Lifestyle & Services' },
  { value: 'Others', label: 'Others' }
];

// Voucher type values aligned with VIO's VOUCHER_TYPES
// (vio-v4-api/src/config/constants.js). VIO is the source of truth.
export const VOUCHER_TYPE_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'discount', label: 'Discount' },
  { value: 'product', label: 'Product' },
  { value: 'cash_discount', label: 'Cash Discount' }
];

// Provider-synced catalog values (Zhongmin etc.). Create form stays VIO-only;
// edit form merges these so the stored value is visible. Value stays
// "Direct Charge" to match the DB enum; the label is Direct Recharge (直充).
export const PROVIDER_VOUCHER_TYPE_OPTIONS = [
  { value: 'Direct Charge', label: 'Direct Recharge' },
  { value: 'Card Voucher', label: 'Card Voucher' },
];

export const CONSUMPTION_TYPE_OPTIONS = [
  { value: 'URL', label: 'URL' },
  { value: 'QR Code', label: 'QR Code' },
  { value: 'Coupon Code', label: 'Coupon Code' },
  { value: 'Manual', label: 'Manual' }
];

export const PROVIDER_CONSUMPTION_TYPE_OPTIONS = [
  { value: 'Direct', label: 'Direct Recharge' },
  { value: 'Code', label: 'Code' },
];

function mergeSelectOptions(base, extras, currentValue) {
  const out = [];
  const seen = new Set();
  for (const option of [...base, ...extras]) {
    if (!option || seen.has(option.value)) continue;
    seen.add(option.value);
    out.push(option);
  }
  if (currentValue && !seen.has(currentValue)) {
    out.push({ value: currentValue, label: currentValue });
  }
  return out;
}

export const voucherTypeSelectOptions = (currentValue) =>
  mergeSelectOptions(VOUCHER_TYPE_OPTIONS, PROVIDER_VOUCHER_TYPE_OPTIONS, currentValue);

export const consumptionTypeSelectOptions = (currentValue) =>
  mergeSelectOptions(
    CONSUMPTION_TYPE_OPTIONS,
    PROVIDER_CONSUMPTION_TYPE_OPTIONS,
    currentValue,
  );

// Types whose stock is a fixed pool of merchant-issued values uploaded by CSV.
// Mirrors backend/utils/consumptionType.js — keep the two in sync.
// Manual has no per-unit secret, so its codes are minted on demand instead.
export const POOL_BACKED_CONSUMPTION_TYPES = ['Coupon Code', 'QR Code', 'URL'];

export const isPoolBackedConsumptionType = (consumptionType) =>
  POOL_BACKED_CONSUMPTION_TYPES.includes(consumptionType);

// URL vouchers: one link per unit from a CSV pool ('unique'), or one link
// shared by every unit ('shared', codes minted per unit). Shared is opt-in only.
export const URL_MODE_OPTIONS = [
  { value: 'unique', label: 'One link per unit (upload CSV)' },
  { value: 'shared', label: 'One shared link for all units' },
];

export const isSharedUrlVoucher = ({ consumptionType, urlMode } = {}) =>
  consumptionType === 'URL' && urlMode === 'shared';

export const isPoolBackedVoucher = ({ consumptionType, urlMode } = {}) =>
  isPoolBackedConsumptionType(consumptionType) &&
  !isSharedUrlVoucher({ consumptionType, urlMode });

// User-facing copy for the CSV pool, per type.
const CONSUMPTION_CSV_COPY = {
  'Coupon Code': {
    singular: 'coupon code',
    plural: 'coupon codes',
    uploadLabel: 'Coupon Code CSV',
    poolTitle: 'VIO Coupon Code Pool (CSV)',
    hint: 'One coupon code per line — the whole line is the code.'
  },
  'QR Code': {
    singular: 'QR payload',
    plural: 'QR payloads',
    uploadLabel: 'QR Payload CSV',
    poolTitle: 'VIO QR Payload Pool (CSV)',
    hint: 'One QR payload per line — the exact string the member app encodes into the QR.'
  },
  URL: {
    singular: 'redemption URL',
    plural: 'redemption URLs',
    uploadLabel: 'Redemption URL CSV',
    poolTitle: 'VIO Redemption URL Pool (CSV)',
    hint: 'One http(s) link per line — each member receives a different one-time URL.'
  }
};

export const getConsumptionCsvCopy = (consumptionType) =>
  CONSUMPTION_CSV_COPY[consumptionType] || CONSUMPTION_CSV_COPY['Coupon Code'];

export const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD - US Dollar' },
  { value: 'EUR', label: 'EUR - Euro' },
  { value: 'GBP', label: 'GBP - British Pound' },
  { value: 'CAD', label: 'CAD - Canadian Dollar' },
  { value: 'AUD', label: 'AUD - Australian Dollar' },
  { value: 'JPY', label: 'JPY - Japanese Yen' },
  { value: 'CNY', label: 'CNY - Chinese Yuan' },
  { value: 'KRW', label: 'KRW - South Korean Won' },
  { value: 'SGD', label: 'SGD - Singapore Dollar' },
  { value: 'HKD', label: 'HKD - Hong Kong Dollar' },
  { value: 'THB', label: 'THB - Thai Baht' },
  { value: 'MYR', label: 'MYR - Malaysian Ringgit' },
  { value: 'IDR', label: 'IDR - Indonesian Rupiah' },
  { value: 'PHP', label: 'PHP - Philippine Peso' },
  { value: 'VND', label: 'VND - Vietnamese Dong' },
  { value: 'INR', label: 'INR - Indian Rupee' },
  { value: 'BRL', label: 'BRL - Brazilian Real' },
  { value: 'MXN', label: 'MXN - Mexican Peso' }
];

// ---------------------------------------------------------------------------
// Expiry mode
// ---------------------------------------------------------------------------
// A voucher expires one of two ways, and the two are mutually exclusive in VIO:
//
//   relative   — "valid N days from purchase". Set when redemptionValidityDays
//                holds a positive whole number. Third-party vouchers have always
//                worked this way; internal inventory can too (the WOGI HK
//                catalogue is sold as "valid 30 days from purchase").
//   fixed_date — expires on expiryEndDatetime, whatever the purchase date.
//
// Third-party vouchers with no window fall back to VIO's approved 30-day
// default. Internal vouchers with no window use their fixed dates instead, so
// the default must not be shown for them.
export const VOUCHER_EXPIRY_MODES = {
  RELATIVE: 'relative',
  FIXED_DATE: 'fixed_date',
};

/** VIO's approved fallback for a third-party voucher with no explicit window. */
export const DEFAULT_EXTERNAL_REDEMPTION_DAYS = 30;

/** Mirrors the backend's serializeRedemptionValidityDays. */
export const getRedemptionWindowDays = (voucher) => {
  const days = voucher?.redemptionValidityDays;
  const n = Number(days);
  if (days === null || days === undefined || days === '') return null;
  return Number.isInteger(n) && n >= 1 ? n : null;
};

/**
 * How this voucher actually expires, and on what basis.
 *
 * `isDefaulted` marks a third-party voucher riding VIO's 30-day default rather
 * than a window someone set, so the UI can label it as inherited.
 */
export const resolveVoucherExpiryMode = (voucher) => {
  const days = getRedemptionWindowDays(voucher);
  if (days !== null) {
    return { mode: VOUCHER_EXPIRY_MODES.RELATIVE, days, isDefaulted: false };
  }
  if (voucher?.isExternalVoucher) {
    return {
      mode: VOUCHER_EXPIRY_MODES.RELATIVE,
      days: DEFAULT_EXTERNAL_REDEMPTION_DAYS,
      isDefaulted: true,
    };
  }
  return { mode: VOUCHER_EXPIRY_MODES.FIXED_DATE, days: null, isDefaulted: false };
};
